import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { GsdReadableBlock } from "@/coquille-bridge";
import { DataTable } from "@/components/DataTable.jsx";
import { getDataStore } from "@/data/dataStore.js";
import { useMembershipCopy, useMembershipStore } from "@/membership/useMembership.js";
import { filterEventsForViewer, mapsDirectionsUrl } from "@/membership/membershipCalendar.js";
import { canViewBlackDragonContent } from "@/membership/blackDragonEngine.js";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { BD_CONTENT_VISIBILITY } from "@/membership/membershipConstants.js";

export function PublicCalendarPage() {
  const { copy } = useMembershipCopy();
  const { localizeDemo } = useKarateUi();
  const store = getDataStore();
  const events = useMemo(
    () =>
      filterEventsForViewer(store.events, { role: "guest" }).map((e) => ({
        ...e,
        titre: localizeDemo(e.titre),
        place: localizeDemo(store.dojos.find((d) => d.id === e.dojo_id)?.nom || "—"),
      })),
    [store, localizeDemo],
  );
  const c = copy.publicCalendar;
  return (
    <PageSection title={c.title} subtitle={c.subtitle} testId="page-calendrier-public">
      <div className="membership-page" data-surface="light">
        <DataTable
          rows={events}
          columns={[
            { key: "date", label: c.columns.date, sortable: true },
            { key: "titre", label: c.columns.title, sortable: true },
            { key: "type", label: c.columns.type, sortable: true },
            { key: "place", label: c.columns.place, sortable: true },
          ]}
          searchFields={["titre", "type", "place", "date"]}
          filters={[{ field: "type", label: c.columns.type, options: [...new Set(events.map((e) => e.type))].map((v) => ({ value: v, label: v })) }]}
          emptyMessage={c.empty}
          emptyFilteredMessage={c.empty}
          testId="public-calendar-table"
        />
      </div>
    </PageSection>
  );
}

export function PublicBlackDragonPage() {
  const { locale, copy } = useMembershipCopy();
  const { localizeDemo } = useKarateUi();
  const store = useMembershipStore();
  const viewer = { role: "guest", isAuthenticated: false, isActiveMember: false, isBlackDragon: false };
  const items = store.blackDragonContent.filter((item) => canViewBlackDragonContent(item, viewer));
  return (
    <PageSection title={copy.publicBd.title} subtitle={copy.publicBd.subtitle} testId="page-black-dragon-public">
      <GsdReadableBlock className="membership-page" testId="public-bd-panel">
        <p data-testid="bd-must-be-member">{copy.publicBd.mustBeMember}</p>
        <p>
          <Link to="/devenir-membre" className="btn-primary" data-testid="bd-become-member-link">
            {copy.homeCta}
          </Link>
        </p>
        {items.length === 0 ? (
          <p data-testid="public-bd-empty">{copy.publicBd.empty}</p>
        ) : (
          items.map((item) => (
            <article key={item.id} className="membership-content-card" data-testid={`bd-content-${item.id}`}>
              <h3>{localizeDemo(locale === "en" ? item.titleEn : item.titleFr)}</h3>
              <p>{localizeDemo(locale === "en" ? item.bodyEn : item.bodyFr)}</p>
              <span className="membership-badge">{copy.vis[item.visibility] || item.visibility}</span>
            </article>
          ))
        )}
        <p className="muted" data-testid="bd-private-hidden-public">
          {copy.portal.bdHidden}
        </p>
      </GsdReadableBlock>
    </PageSection>
  );
}

export function DojoDirectionsLink({ dojo, label }) {
  const url = mapsDirectionsUrl(dojo);
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="btn-secondary btn-sm" data-testid={`dojo-directions-${dojo.id}`}>
      {label}
    </a>
  );
}

export { BD_CONTENT_VISIBILITY };
